import type { RefObject } from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import {
	type GradingPanelLayout,
	computeGradingPanelLayout,
	measureNaturalPanelHeight,
	preMeasureGradingPanelLayout,
} from "./playerGradingPanelLayout";

/**
 * Positions a fixed grading panel under a trigger, measures intrinsic height, and
 * expands into the viewport only when content does not fit below the trigger.
 */
export function usePlayerGradingPanelLayout(
	isOpen: boolean,
	triggerRef: RefObject<HTMLElement | null>,
	panelRef: RefObject<HTMLElement | null>,
	/** When this value changes while open, height is re-measured (e.g. form/header content). */
	contentLayoutKey: string,
): { panelLayout: GradingPanelLayout | null; invalidatePanelSize: () => void } {
	const [layoutTick, setLayoutTick] = useState(0);
	const [naturalHeight, setNaturalHeight] = useState<number | null>(null);

	const invalidatePanelSize = useCallback(() => {
		setNaturalHeight(null);
	}, []);

	useEffect(() => {
		if (!isOpen) {
			setNaturalHeight(null);
			return;
		}
		const bump = () => setLayoutTick((n) => n + 1);
		window.addEventListener("resize", bump);
		document.addEventListener("scroll", bump, true);
		window.visualViewport?.addEventListener("resize", bump);
		return () => {
			window.removeEventListener("resize", bump);
			document.removeEventListener("scroll", bump, true);
			window.visualViewport?.removeEventListener("resize", bump);
		};
	}, [isOpen]);

	const panelLayout = useMemo((): GradingPanelLayout | null => {
		void layoutTick;
		void contentLayoutKey;
		if (!isOpen || typeof window === "undefined" || !triggerRef.current) {
			return null;
		}
		const rect = triggerRef.current.getBoundingClientRect();
		if (naturalHeight == null) {
			return preMeasureGradingPanelLayout(rect);
		}
		return computeGradingPanelLayout(rect, naturalHeight);
	}, [isOpen, layoutTick, naturalHeight, contentLayoutKey]);

	useLayoutEffect(() => {
		if (!isOpen || !panelRef.current) {
			return;
		}
		const measured = measureNaturalPanelHeight(panelRef.current);
		if (measured <= 0) {
			return;
		}
		setNaturalHeight((prev) => {
			if (prev == null) {
				return measured;
			}
			return Math.abs(measured - prev) > 1 ? measured : prev;
		});
	}, [isOpen, layoutTick, contentLayoutKey]);

	return { panelLayout, invalidatePanelSize };
}
